import { useCallback, useSyncExternalStore } from 'react';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { getTramite } from '@/services/tramiteService';
import { reportApiError } from '@/common/utils/apiError';
import { KANBAN_QUERY_KEY } from './kanbanUtils';
import type {
  EscrituraKanbanStatus,
  KanbanPage,
  TramiteKanbanItem,
} from './types';

type KanbanQueryData = InfiniteData<KanbanPage> | undefined;

/**
 * Relaciones que el endpoint de kanban no manda. Las respuestas de los PATCH sí
 * las traen, y meterlas en la caché haría que las tarjetas del tablero tengan
 * una forma distinta según hayan sido tocadas o no. `presupuesto` sí viaja
 * (recortado, para los indicadores), así que se deja pasar para no pisarlo con
 * datos viejos.
 */
const NON_KANBAN_KEYS = ['user', 'parametros'] as const;

/**
 * Deja la respuesta de un endpoint de detalle con la misma forma que las filas
 * del tablero, para poder mezclarla sin ensuciar la caché.
 */
export const toKanbanPatch = (tramite: unknown): Partial<TramiteKanbanItem> => {
  if (!tramite || typeof tramite !== 'object') return {};

  const patch = { ...(tramite as Record<string, unknown>) };
  NON_KANBAN_KEYS.forEach((key) => delete patch[key]);

  return patch as Partial<TramiteKanbanItem>;
};

const mapPages = (
  data: KanbanQueryData,
  mapPage: (page: KanbanPage) => KanbanPage | null,
): KanbanQueryData => {
  if (!data?.pages?.length) return data;

  let changed = false;
  const pages = data.pages.map((page) => {
    const next = mapPage(page);
    if (next && next !== page) changed = true;
    return next ?? page;
  });

  // Devolver un objeto nuevo cuando nada cambió obliga a React Query a
  // re-renderizar las cuatro columnas sin motivo.
  return changed ? { ...data, pages } : data;
};

/**
 * Mezcla los campos cambiados de una escritura en todas las columnas donde esté
 * cacheada, sin pedir nada al servidor.
 *
 * Antes, cualquier edición invalidaba `['tramites','kanban']`, y React Query
 * revalida TODAS las páginas cargadas de las cuatro columnas, en secuencia: un
 * clic con 10 tandas cargadas disparaba ~40 requests.
 */
export const patchKanbanTramite = (
  queryClient: QueryClient,
  tramiteId: number | string,
  patch: Partial<TramiteKanbanItem>,
): void => {
  if (tramiteId == null || !patch || Object.keys(patch).length === 0) return;

  queryClient.setQueriesData<KanbanQueryData>(
    { queryKey: KANBAN_QUERY_KEY },
    (data) => mapPages(data, (page) => {
      const index = page.data.findIndex((row) => String(row.id) === String(tramiteId));
      if (index === -1) return null;

      const rows = [...page.data];
      rows[index] = { ...rows[index], ...patch };

      return { ...page, data: rows };
    }),
  );
};

/**
 * Saca una escritura del tablero (se finalizó, se eliminó, o dejó de cumplir el
 * filtro activo) y ajusta el contador de la columna.
 */
export const removeKanbanTramite = (
  queryClient: QueryClient,
  tramiteId: number | string,
): void => {
  if (tramiteId == null) return;

  queryClient.setQueriesData<KanbanQueryData>(
    { queryKey: KANBAN_QUERY_KEY },
    (data) => {
      if (!data?.pages?.length) return data;

      const removed = data.pages.reduce(
        (count, page) => count + page.data.filter((row) => String(row.id) === String(tramiteId)).length,
        0,
      );

      if (removed === 0) return data;

      // El total se descuenta en todas las páginas, no sólo en la que tenía la
      // fila: la columna muestra el total de una página cualquiera y si quedan
      // desalineados el contador del encabezado depende de cuál se lea.
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          data: page.data.filter((row) => String(row.id) !== String(tramiteId)),
          total: Math.max(0, page.total - removed),
        })),
      };
    },
  );
};

/**
 * Mueve una escritura de columna: la saca de donde estuviera cacheada y deja la
 * columna destino en su primera página para que vuelva a entrar en su posición
 * según el orden vigente.
 *
 * Hace falta porque las columnas son excluyentes: al bloquear o desbloquear, un
 * `patchKanbanTramite` dejaría la tarjeta en una columna que ya no le
 * corresponde. Se resetea sólo la columna destino (una request) en vez de
 * invalidar el tablero entero.
 */
export const relocateKanbanTramite = (
  queryClient: QueryClient,
  tramiteId: number | string,
  targetStatus: EscrituraKanbanStatus,
): Promise<void> => {
  if (tramiteId == null) return Promise.resolve();

  removeKanbanTramite(queryClient, tramiteId);

  return queryClient.resetQueries({
    queryKey: [...KANBAN_QUERY_KEY, targetStatus],
  });
};

/**
 * Trae una sola escritura y la mezcla en el tablero. Se usa cuando el cambio no
 * devuelve el trámite actualizado (por ejemplo el checklist de tareas, que
 * cambia `tareas_completadas_count`): una request en vez de revalidar todo.
 */
export const refreshKanbanTramite = async (
  queryClient: QueryClient,
  tramiteId: number | string,
): Promise<void> => {
  if (tramiteId == null) return;

  try {
    const tramite = await getTramite({ id: tramiteId });
    patchKanbanTramite(queryClient, tramiteId, toKanbanPatch(tramite));
  } catch (error) {
    // Que falle el refresco puntual no puede romper el flujo: la tarjeta queda
    // con los datos previos hasta la próxima carga, pero queda registrado.
    reportApiError(error, {
      operation: 'kanban:refrescar-tarjeta',
      extra: { tramiteId },
    });
  }
};

/**
 * Recarga el tablero desde cero. `resetQueries` (y no `invalidateQueries`)
 * a propósito: deja cada columna en su primera página, así el costo es de 4
 * requests fijas en vez de una por cada tanda que el usuario haya scrolleado.
 *
 * Se reserva para cambios que reordenan el tablero entero (filtros, alta de una
 * escritura). Para editar una tarjeta existen las funciones de arriba.
 */
export const resetKanbanBoard = (queryClient: QueryClient): Promise<void> =>
  queryClient.resetQueries({ queryKey: KANBAN_QUERY_KEY });

/**
 * Busca una escritura en la caché del tablero. Devuelve la referencia guardada
 * tal cual (no una copia), que es lo que permite usarla con
 * `useSyncExternalStore` sin loops de render.
 */
export const findKanbanTramite = (
  queryClient: QueryClient,
  tramiteId: number | string | null | undefined,
): TramiteKanbanItem | undefined => {
  if (tramiteId == null) return undefined;

  const entries = queryClient.getQueriesData<KanbanQueryData>({ queryKey: KANBAN_QUERY_KEY });

  for (const [, data] of entries) {
    for (const page of data?.pages ?? []) {
      const match = page.data.find((row) => String(row.id) === String(tramiteId));
      if (match) return match;
    }
  }

  return undefined;
};

/**
 * Versión viva de una tarjeta del tablero.
 *
 * Los diálogos (participantes, responsable) recibían el objeto capturado en el
 * momento del clic, así que seguían mostrando los datos viejos si algo los
 * actualizaba mientras estaban abiertos. Devuelve `undefined` si la escritura
 * salió del tablero, para que quien lo use pueda cerrar el diálogo.
 */
export const useKanbanTramite = (
  tramiteId: number | string | null | undefined,
): TramiteKanbanItem | undefined => {
  const queryClient = useQueryClient();

  const subscribe = useCallback(
    (onStoreChange: () => void) => queryClient.getQueryCache().subscribe(onStoreChange),
    [queryClient],
  );

  const getSnapshot = useCallback(
    () => findKanbanTramite(queryClient, tramiteId),
    [queryClient, tramiteId],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
